import { createAsyncThunk, createSlice, type PayloadAction } from "@reduxjs/toolkit"

import { getDashboardNba, getDashboardPortfolio } from "../api/services.ts"
import { EMPTY_GLOBAL_FILTERS } from "./globalFilters.ts"
import type {
  GlobalFilters,
  NbaItem,
  PortfolioBundle,
  PortfolioState,
  PortfolioStatus,
  PortfolioWindow,
  PortfolioWindowPreset
} from "./portfolio.types.ts"

export const DEFAULT_WINDOW_PRESET: PortfolioWindowPreset = "this_month"

const unwrap = <T>(data: unknown): T | undefined => {
  const enveloped = (data as { details?: { data?: T } } | undefined)?.details?.data
  return enveloped ?? (data as T | undefined)
}

const initialState: PortfolioState = {
  bundle: null,
  nba: [],
  nbaTotal: 0,
  window: { preset: DEFAULT_WINDOW_PRESET },
  globalFilters: EMPTY_GLOBAL_FILTERS,
  status: "idle",
  currentRequestId: undefined
}

type FetchPortfolioResult =
  | { status: "available"; bundle: PortfolioBundle }
  | { status: "unavailable"; bundle: null }

export const fetchPortfolio = createAsyncThunk<FetchPortfolioResult, PortfolioWindow>(
  "portfolio/fetchPortfolio",
  async (window, { getState, rejectWithValue }) => {
    const filters =
      (getState() as { portfolio?: PortfolioState }).portfolio?.globalFilters ??
      EMPTY_GLOBAL_FILTERS
    const response = await getDashboardPortfolio(window, filters)

    if (response.status === 404) {
      return { status: "unavailable", bundle: null }
    }

    if (!response.ok) {
      return rejectWithValue(response.data)
    }

    const bundle = unwrap<PortfolioBundle>(response.data)
    if (!bundle || !Array.isArray(bundle.tiles)) {
      return rejectWithValue({ detail: "malformed portfolio response" })
    }
    return { status: "available", bundle }
  }
)

export const fetchNba = createAsyncThunk<
  { items: NbaItem[]; total_unfiltered: number }
>("portfolio/fetchNba", async (_, { rejectWithValue }) => {
  const response = await getDashboardNba()

  if (!response.ok) {
    return rejectWithValue(response.data)
  }

  const payload = unwrap<{ items: NbaItem[]; total_unfiltered: number }>(response.data)
  return { items: payload?.items ?? [], total_unfiltered: payload?.total_unfiltered ?? 0 }
})

const portfolioSlice = createSlice({
  name: "portfolio",
  initialState,
  reducers: {
    setWindow(state, action: PayloadAction<PortfolioWindow>) {
      state.window = action.payload
    },
    setGlobalFilters(state, action: PayloadAction<GlobalFilters>) {
      state.globalFilters = action.payload
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPortfolio.pending, (state, action) => {
        state.status = "loading"
        state.currentRequestId = action.meta?.requestId
      })
      .addCase(fetchPortfolio.fulfilled, (state, action) => {
        if (action.meta?.requestId !== state.currentRequestId) return
        const { status, bundle } = action.payload
        state.status = status as PortfolioStatus
        state.bundle = bundle
      })
      .addCase(fetchPortfolio.rejected, (state, action) => {
        if (action.meta?.requestId !== state.currentRequestId) return
        state.status = "error"
      })

      .addCase(fetchNba.fulfilled, (state, action) => {
        state.nba = action.payload.items
        state.nbaTotal = action.payload.total_unfiltered
      })
      .addCase(fetchNba.rejected, () => {})
  }
})

export const { setWindow, setGlobalFilters } = portfolioSlice.actions

export default portfolioSlice
